<script>
  import {
    execution,
    toolbar,
    definitions as definitionStore,
    codegenError,
    stepWarnings,
    viewInCode,
  } from "../store";

  import { Icon, ArrowUturnLeft, ChatBubbleBottomCenterText } from "svelte-hero-icons";
  import OpenDocumentation from "$lib/Elements/OpenDocumentation.svelte";
  import Pane from "$lib/Elements/Pane.svelte";
  import CopyText from "$lib/Elements/CopyText.svelte";
  import Debug from "$lib/Elements/Debug.svelte";
  import CheckboxInput from "$lib/Inputs/CheckboxInput.svelte";
  import InlineEdit from "$lib/Inputs/InlineEdit.svelte";
  import InputWrapper from "./InputWrapper.svelte";
  import StepState from "../Workflow/StepState.svelte";
  import LuaError from "../Workflow/LuaError.svelte";
  import { persisted } from "$lib/utils/persisted-stores";
  import CommentThread from "./CommentThread.svelte";

  let workflow;
  // svelte-ignore unused-export-let
  export { workflow as definition };
  export let context;
  export let step;
  export let isReadonly;

  let toggleTextInput; // callback from CommentThread

  const stepDefinition = definitionStore.getDefinition(step.type);
  const state = execution.stepState(step.id);
  $: showStepState = $state && $state.status !== null;

  const additionalHelp = persisted("show_additional_help", true);
  $: showAdditionalHelp = $additionalHelp;
  $: stepWarning = ($stepWarnings ?? []).find((item) => item.id === step?.id) ?? null;

  // Handle missing step definitions gracefully
  const { doc, link, deprecated, unavailable } = stepDefinition?.metadata || {};
  const { properties, componentType, type } = step;
  const metadata = step.metadata || {};

  function handleAddComment(event) {
    const newComment = event.detail;
    step.comments = [...(step.comments || []), newComment];
    context.notifyPropertiesChanged();
  }

  function handleDeleteComment(event) {
    const { index } = event.detail;
    step.comments = step.comments.filter((_, i) => i !== index);
    context.notifyPropertiesChanged();
  }

  function handleResolveComment(event) {
    const { index, resolver } = event.detail;
    step.comments[index].resolved = true;
    step.comments[index].resolver = resolver;
    context.notifyPropertiesChanged();
  }

  function onPropertyChanged(property, value) {
    const branchesById = getBranchesById(properties["branches"], step.branches);

    // Handle composite values from EnterTextInput
    if (
      value &&
      typeof value === "object" &&
      value.keyboard !== undefined &&
      value.text !== undefined
    ) {
      properties.keyboard = value.keyboard;
      properties.text = value.text;
    } else {
      properties[property] = value;
    }

    context.notifyPropertiesChanged();

    if (stepDefinition?.inputs?.[property]?.name == "branches") {
      step.branches = updateBranches(branchesById, value);
      context.notifyChildrenChanged();
    }
  }

  function onMetaChanged(property, value) {
    step.metadata = step.metadata || {};
    step.metadata[property] = value;
    context.notifyPropertiesChanged();
  }

  function onNameChange(newName) {
    step.name = newName || stepDefinition?.name || step.type;
    context.notifyNameChanged();
  }

  function resetName() {
    if (isReadonly) return;
    step.name = stepDefinition?.name || step.type;
    context.notifyNameChanged();
  }

  function getBranchesById(oldBranchDef, branches) {
    if (!oldBranchDef || !branches) return;

    return Object.entries(branches).reduce((acc, [branch_name, branch]) => {
      const branchDef = oldBranchDef.find((b) => b.name === branch_name);
      if (branchDef && branchDef.id) {
        acc[branchDef.id] = [...branch];
      }
      return acc;
    }, {});
  }

  function updateBranches(branchesById, newSteps) {
    return newSteps.reduce((acc, branch) => {
      acc[branch.name] = [...(branchesById[branch.id] || [])];
      return acc;
    }, {});
  }
</script>

<Pane>
  <div
    class={$toolbar.hide_props && showStepState
      ? "max-h-6 overflow-hidden relative gradient-overlay"
      : ""}
  >
    <div class="border-b border-line-strong px-3 pb-4 mb-4">
      <h3 class="font-semibold mb-2 text-content text-base flex items-center">
        <InlineEdit
          value={step.name || stepDefinition?.name || step.type}
          on:change={(e) => onNameChange(e.detail)}
          class="block w-full text-left hover:outline outline-1 outline-slate-200 rounded-xs -mx-1 px-1"
          doubleClick={false}
          {isReadonly}
        />
        <button
          on:click={resetName}
          class="ml-2 text-xs text-content-subtle hover:text-content focus:outline-none"
          title="Reset to default name"
        >
          <Icon src={ArrowUturnLeft} class="w-3 h-3" />
        </button>
      </h3>

      <div class="flex items-center space-x-2">
        <CopyText text={type} class="text-xs" />
        {#if link}
          <OpenDocumentation {link} />
        {/if}
        <button
          on:click={toggleTextInput}
          class="bg-transparent border-none cursor-pointer p-0 text-content-subtle"
        >
          <Icon src={ChatBubbleBottomCenterText} class="w-4 h-4 -ml-1" />
        </button>
      </div>
      <div class="flex items-center space-x-2 mt-1">
        <CopyText
          text={step.id}
          displayText={`step id: ${step.id}`}
          class="text-xs text-content-subtle"
        />
      </div>

      <CommentThread
        bind:toggleTextInput
        {isReadonly}
        comments={step.comments || []}
        on:addComment={handleAddComment}
        on:deleteComment={handleDeleteComment}
        on:resolveComment={handleResolveComment}
      />

      {#if unavailable}
        <div
          data-test="step-unavailable"
          class="mt-2 bg-gradient-to-br from-amber-100 to-amber-200 border-l-4 border-amber-500 rounded-md shadow-sm px-4 py-3"
        >
          <div class="flex items-start gap-2">
            <span class="text-base leading-none">⚠️</span>
            <p class="m-0 text-xs font-medium leading-relaxed text-amber-900">
              {unavailable} This step will fail when the workflow runs.
            </p>
          </div>
        </div>
      {/if}

      {#if deprecated}
        <div
          class="mt-2 bg-gradient-to-br from-amber-100 to-amber-200 border-l-4 border-amber-500 rounded-md shadow-sm px-4 py-3"
        >
          <div class="flex items-start gap-2">
            <span class="text-base leading-none">⚠️</span>
            <div
              class="text-xs text-amber-900 flex-1 [&_p]:m-0 [&_p]:text-amber-900 [&_p]:text-xs [&_p]:leading-relaxed [&_p]:font-medium [&_strong]:text-amber-900 [&_strong]:font-semibold"
            >
              <!-- eslint-disable-next-line -->
              {@html deprecated}
            </div>
          </div>
        </div>
      {/if}

      {#if doc && showAdditionalHelp}
        <p class="mt-2 text-xs text-content-subtle prose prose-slate">
          <!-- eslint-disable-next-line -->
          {@html doc}
        </p>
      {/if}

      <div class="mt-3 flex flex-col space-y-1">
        <CheckboxInput
          value={metadata.skip}
          on:input={(e) => onMetaChanged("skip", e.detail)}
          label="Skip block during execution"
          {isReadonly}
        />
        <CheckboxInput
          value={metadata.continueOnError}
          on:input={(e) => onMetaChanged("continueOnError", e.detail)}
          label="Ignore errors, continue execution"
          {isReadonly}
        />
      </div>
    </div>

    <div class="px-3 mb-4">
      <h4 class="font-semibold mb-4">Properties</h4>
      {#if !stepDefinition}
        <div class="text-sm text-amber-600 bg-amber-50 p-2 rounded">
          Unknown step type: <code class="font-mono">{step.type}</code>
        </div>
        <pre class="mt-2 text-xs">{JSON.stringify(step, null, 2)}</pre>
      {:else if ["task", "switch", "container"].includes(componentType) && stepDefinition?.inputs}
        {#each Object.entries(stepDefinition.inputs) as [property, propertyType]}
          <InputWrapper
            {properties}
            {property}
            {propertyType}
            {isReadonly}
            {showAdditionalHelp}
            {step}
            updateName={onNameChange}
            validationWarnings={$state?.validations[property] || null}
            on:change={(e) => onPropertyChanged(property, e.detail)}
          />
        {/each}
      {:else}
        <pre>{JSON.stringify(step, null, 2)}</pre>
      {/if}
    </div>
  </div>

  {#if stepWarning}
    <div class="mb-4 rounded border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      {stepWarning.message}
    </div>
  {/if}

  {#if $codegenError?.id === step.id && $codegenError.structured}
    <div class="mb-4">
      <LuaError error={$codegenError.structured} onJumpToLine={viewInCode} />
    </div>
  {/if}

  {#if showStepState}
    <StepState state={$state} />
  {/if}

  {#if $toolbar.show_debug}
    <Debug state={$state} {step} />
  {/if}
</Pane>

<style>
  .gradient-overlay:before {
    content: "";
    background: linear-gradient(0, white, transparent);
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 1;
  }
</style>
